/**
 * Which of the sales points this call covered.
 *
 * The co-pilot has always computed this DURING a call, to decide what to
 * suggest next, and then thrown it away on hangup - so "did we ask about the
 * budget" was answerable for the fifteen minutes the panel was open and never
 * again. This is the same answer, kept.
 *
 * Read-only, and that is a decision rather than a missing feature. The mark says
 * what was heard on the call; letting an operator tick it by hand would turn a
 * measurement into a self-assessment, and the report built on it into a record
 * of who remembers to tick boxes. If the reading is wrong, the transcript is
 * right there underneath it.
 *
 * Draws nothing at all when nobody has scored the call - see `coverageOf`, where
 * telling that apart from a score of zero is the whole job.
 */
import { Check, ListChecks, Minus } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import type { PilotChecklistItem } from "../../../../lib/callPilot";
import { coverageOf } from "./coverage";

export function CallChecklist({
  items,
  answered,
  readAt,
}: {
  items: readonly PilotChecklistItem[];
  answered: string[] | null | undefined;
  readAt: string | null | undefined;
}) {
  const t = useT();
  const coverage = coverageOf(items, answered, readAt);
  if (!coverage) return null;

  return (
    <div className="crm-cov">
      <div className="crm-cov-t">
        <ListChecks className="size-4" />
        {t("calls.detail.coverage")}
        {/* Digits, not a translated string. Estonian would pluralise the noun
            off the NUMERATOR here and read "1/6 punkt", which is wrong for a
            fraction - the count that governs is the one nobody is looking at. */}
        <span className="crm-cov-n">
          {coverage.covered}/{coverage.total}
        </span>
      </div>
      <ul className="crm-cov-l">
        {coverage.rows.map((row) => (
          <li key={row.id} className={cn("crm-cov-r", row.covered && "is-on")}>
            {row.covered ? (
              <Check className="size-3.5 shrink-0" />
            ) : (
              <Minus className="size-3.5 shrink-0" />
            )}
            <span>{row.question}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
